/**
 * API client for Business Units
 * Used for organizational hierarchy and incident ownership
 */

import { apiRequest } from './standard-api';

export interface BusinessUnit {
  id: string;
  name: string;
  code?: string;
  description?: string;
  parentId?: string;
  managerId?: string;
  isActive: boolean;
  level?: number;
  createdAt: string;
  updatedAt: string;
}

interface BusinessUnitListResponse {
  success: boolean;
  data: BusinessUnit[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
}

interface BusinessUnitListParams {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/**
 * List business units with optional filtering and pagination
 * Used for lookup/search in incident forms
 */
export async function listBusinessUnits(params: BusinessUnitListParams = {}): Promise<BusinessUnitListResponse> {
  const queryParams = new URLSearchParams();
  
  if (params.page) queryParams.set('page', String(params.page));
  if (params.limit) queryParams.set('limit', String(params.limit));
  if (params.search) queryParams.set('search', params.search);
  if (params.isActive !== undefined) queryParams.set('isActive', String(params.isActive));
  if (params.sortBy) queryParams.set('sortBy', params.sortBy);
  if (params.sortOrder) queryParams.set('sortOrder', params.sortOrder);

  const response = await apiRequest<BusinessUnitListResponse>(
    `/api/v1/business-units${queryParams.toString() ? `?${queryParams.toString()}` : ''}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }
  );

  if (!response.data) {
    throw new Error('Failed to load business units');
  }

  return response.data;
}

/**
 * Get business unit by ID
 */
export async function getBusinessUnit(id: string): Promise<BusinessUnit> {
  const response = await apiRequest<BusinessUnit>(
    `/api/v1/business-units/${id}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }
  );

  if (!response.data) {
    throw new Error('Failed to load business unit');
  }

  return response.data;
}

/**
 * Search business units by name (for autocomplete)
 */
export async function searchBusinessUnits(query: string, limit: number = 10): Promise<BusinessUnit[]> {
  const response = await listBusinessUnits({
    search: query,
    limit,
    isActive: true,
    sortBy: 'name',
    sortOrder: 'asc'
  });

  return response.data;
}
